import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { ArrowForwardIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@dfds-frontend/navigator-icons/icons'

import { useTravelDates } from '../../Hooks/dates/useTravelDates'
import { useThreeDots } from '../../Hooks/useThreeDots'
import { useWindowWidth2 } from '../../Hooks/useWindowWidth2'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { toShortDateString } from '../../Utils/date'
import OverlayFooter from './OverlayDateFooter'

interface Props {
  children: React.ReactNode
  dataAttrList: { name: string; value?: string }[]
  disabled?: boolean
  fieldName: string
  isLoading: boolean
  isOpen: boolean
  onClick: (clickedOutside: boolean, clickedReturnField?: boolean) => void
  onClose: () => void
  showFooter: boolean
  triggerRef: React.RefObject<HTMLButtonElement | null>
}

const DateField: React.FC<Props> = ({ children, dataAttrList, disabled, fieldName, isLoading, isOpen, onClick, onClose, triggerRef }) => {
  const { selectedHomeboundDate, selectedOutboundDate } = useTravelDates()
  const {
    state: { isOneWay },
  } = useTravelSearchContext()
  const { t } = useTranslation()
  const loadingDots = useThreeDots()
  const { isMobile, isTablet } = useWindowWidth2()
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = `${fieldName}-panel`

  const dataAttrProps = {} as ComponentProps
  dataAttrList.map((dataAttr) => {
    const key = `data-e2e-${dataAttr?.name}`
    const dataAttrKey = key as keyof ComponentProps
    dataAttrProps[dataAttrKey] = dataAttr.value?.toString() ?? ''
  })
  const keyLoading = `data-e2e-${fieldName}-loading`
  const dataAttrKeyLoading = keyLoading as keyof ComponentProps
  dataAttrProps[dataAttrKeyLoading] = isLoading.toString()

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        if (!(event?.target?.name === 'day' && event?.target?.type === 'button')) {
          // This is a hack to prevent the datepicker from closing when clicking on a day in the calendar on offers because a rerender happens when homebound dates are loaded
          onClick(true)
        }
      }
    }

    if (isOpen && !isMobile && !isTablet && ref) addEventListener('click', handleClickOutside)
    if (!isOpen && ref) window.removeEventListener('click', handleClickOutside)
    return () => window.removeEventListener('click', handleClickOutside)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ref])

  // Hand the calendar its own focus so react-day-picker's arrow-key navigation works straight away.
  useEffect(() => {
    if (!isOpen) return
    const panel = panelRef.current
    const day =
      panel?.querySelector<HTMLButtonElement>('button[name="day"][tabindex="0"]') ??
      panel?.querySelector<HTMLButtonElement>('button[name="day"]:not([disabled])')
    day?.focus()
  }, [isOpen])

  return (
    <div
      className="h-[58px] relative flex h-full flex-row gap-semantic-global-sm lg:gap-0 bg-white"
      id={`${fieldName}-field-container`}
      ref={ref}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
          triggerRef.current?.focus()
        }
      }}
      {...dataAttrProps}
    >
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={clsx(
          'relative box-border flex h-full w-full flex-row items-center gap-semantic-global-sm border border-solid border-semantic-global-border-light bg-white px-semantic-global-xs text-left transition duration-200 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2! focus-visible:outline-semantic-global-primary-default hover:bg-semantic-global-background-light-grey',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
        disabled={disabled}
        id={`${fieldName}-trigger`}
        onClick={() => {
          if (!isOpen && !isLoading && !disabled) {
            onClick(false)
          } else onClose()
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' || isOpen || isLoading || disabled) return
          event.preventDefault()
          onClick(false)
        }}
        ref={triggerRef}
        type="button"
      >
        <div className="flex w-full items-center justify-between gap-semantic-global-sm">
          <div
            className={clsx(
              disabled ? 'cursor-not-allowed text-semantic-global-disabled-default' : 'cursor-pointer text-semantic-global-text-default'
            )}
          >
            <CalendarIcon size="md" />
          </div>
          <div className="flex w-full items-center md:flex-1">
            <div className="flex items-center justify-start gap-2 md:flex-initial">
              <div className="flex flex-col justify-center gap-1 truncate md:flex-initial">
                <span
                  className={clsx(
                    'typography-body-xs-regular select-none',
                    disabled
                      ? 'cursor-not-allowed text-semantic-global-disabled-default'
                      : 'cursor-pointer text-semantic-global-text-default'
                  )}
                >
                  {isOneWay ? t('DEPARTURE-DATE-LABEL') : t('OUTBOUND')}
                </span>
                <span
                  className={clsx(
                    'notranslate w-full flex-none select-none truncate border-none font-medium leading-none focus:outline-none',
                    disabled
                      ? 'cursor-not-allowed text-semantic-global-disabled-default'
                      : 'cursor-pointer text-semantic-global-text-default'
                  )}
                  data-e2e="outbound-date-input"
                  id={`${fieldName}-input`}
                  translate="no"
                >
                  {isLoading ? loadingDots : toShortDateString(selectedOutboundDate)}
                </span>
              </div>
            </div>

            {!isOneWay ? (
              <>
                <div className="px-semantic-global-xs text-semantic-global-disabled-default md:px-semantic-global-md">
                  <ArrowForwardIcon size="md" />
                </div>
                <div className={clsx('flex flex-col justify-center gap-1 truncate md:flex-initial')}>
                  <span
                    className={clsx(
                      'typography-body-xs-regular select-none',
                      disabled
                        ? 'cursor-not-allowed text-semantic-global-disabled-default'
                        : 'cursor-pointer text-semantic-global-text-default'
                    )}
                  >
                    {t('INBOUND')}
                  </span>
                  <span
                    className={clsx(
                      'notranslate w-full flex-none select-none truncate border-none font-medium leading-none focus:outline-none',
                      disabled || isOneWay
                        ? 'cursor-not-allowed text-semantic-global-disabled-default'
                        : 'cursor-pointer text-semantic-global-text-default'
                    )}
                    data-e2e="homebound-date-input"
                    id={`${fieldName}-input`}
                    translate="no"
                  >
                    {isLoading && !isOneWay ? loadingDots : toShortDateString(selectedHomeboundDate)}
                  </span>
                </div>
              </>
            ) : null}
          </div>
          <div className="pointer-events-none ml-auto">{isOpen ? <ChevronUpIcon size="md" /> : <ChevronDownIcon size="md" />}</div>
        </div>
      </button>
      <div
        aria-label={t('SELECT-DATE')}
        className={clsx(
          `z-[2000] bg-white lg:absolute lg:top-[58px] lg:mt-semantic-global-xs lg:bg-white lg:shadow-md lg:drop-shadow-xl max-lg:fixed max-lg:bottom-0 max-lg:left-0 max-lg:right-0 max-lg:top-0 max-lg:w-full`,
          isOpen && 'lg:left-[-450px] xl:left-[-250px]'
        )}
        hidden={!isOpen}
        id={panelId}
        ref={panelRef}
        role="dialog"
        onBlur={(event) => {
          // Clicking non-focusable content (labels, text) blurs to nothing, not to something outside the panel.
          if (!event.relatedTarget) return
          if (event.currentTarget.contains(event.relatedTarget)) return
          // The trigger's own click handler toggles the panel; closing here too would reopen it on a stale isOpen read.
          if (event.relatedTarget === triggerRef?.current) return
          onClose()
        }}
      >
        <div className="flex h-full flex-col lg:p-semantic-global-md">
          <div className="flex h-[64px] min-h-[64px] flex-row items-center pl-semantic-global-md lg:hidden" id="header-content">
            <h3 className="max-w-[350px] typography-heading-md text-semantic-global-text-default">{t('SELECT-DATE')}</h3>
            <button
              aria-label={`${t('CLOSE')} ${t('SELECT-DATE')}`}
              className={`fill-bg-500 ml-auto flex h-full w-12 ${
                isLoading || disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } items-center justify-center border-0 bg-transparent text-semantic-global-text-default focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-semantic-global-primary-default`}
              onClick={() => {
                if (!disabled || isOneWay) {
                  onClick(true)
                  triggerRef.current?.focus()
                }
              }}
              type="button"
            >
              <CloseIcon size="md" />
            </button>
          </div>
          <div className="border-0 border-b border-solid border-gray-300 lg:hidden" />
          <div
            className="lg:bg-white max-lg:h-[calc(100%-128px)] max-lg:overflow-y-scroll"
            id="main-content"
            onKeyDown={(event) => {
              if (event.key !== 'Tab') return
              const target = event.target as HTMLElement
              if (!target.matches('button[name="day"]')) return
              // With at most one visible previous/next month button, jump straight to it instead of leaving the grid.
              const navButton = panelRef.current?.querySelector<HTMLButtonElement>(
                `button[name="${event.shiftKey ? 'previous' : 'next'}-month"]`
              )
              if (!navButton || navButton.disabled) return
              event.preventDefault()
              navButton.focus()
            }}
          >
            {children}
          </div>
          {selectedOutboundDate ? (
            <OverlayFooter isLoading={isLoading} onClick={() => onClick(false)}>
              <div className="flex flex-row items-center" />
            </OverlayFooter>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default DateField

interface ComponentProps {
  dataAttrName: string
  dataAttrValue: string
}
